import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'
import { JetStreamAuthI, NavigationI } from '@/Types/types'
import { DocuStore } from '@/Pinia/DocuStore'
import { router, usePage } from '@inertiajs/vue3'
import { Routes } from '@/Enums/Routes'
import { navigateToRoute } from '@/Composables/navigateToRoute'

export const NavbarStore = defineStore('navbar-store', () => {
    const docuStore = DocuStore()
    const expandedItems = ref<Record<string, boolean>>({})
    const pageProps = computed(() => usePage<{ auth: JetStreamAuthI }>().props)

    const navbar = reactive<NavigationI[]>([
        {
            label: 'Home',
            icon: 'pi pi-fw pi-home text-info',
            to: docuStore.getActiveOrganizationCount > 0 ? '/organization/undefined/dashboard' : '/',
            command: () => {
                if (docuStore.getActiveOrganizationCount > 0) {
                    router.visit(`/organization/${docuStore.activeOrganization?.id}/dashboard`)
                } else {
                    router.visit(route('landing'))
                }
            },
            display: true,
        },
        {
            label: 'Contact Us',
            icon: 'pi pi-fw pi-question-circle',
            to: '/contact-us',
            command: () => {
                router.visit(route('contact-us'))
            },
            display: true,
        },
        {
            label: 'Businesses',
            icon: 'pi pi-fw pi-briefcase text-info',
            to: `/organization/undefined/businesses`,
            command: () => {
                navigateToRoute(Routes.Business, { id: docuStore.getActiveOrganizationId })
            },
            display: () => docuStore.getPermissionToOrganization('business_view'),
            items: [
                {
                    label: 'Create Business',
                    icon: 'pi pi-plus text-info',
                    to: '/organization/undefined/businesses',
                    inlineIconNav: true,
                    command: () => {
                        navigateToRoute(
                            Routes.Business,
                            { id: docuStore.getActiveOrganizationId },
                            'BusinessCreateDialog-open',
                        )
                    },
                    display: () => docuStore.getPermissionToOrganization('business_create'),
                },
                {
                    label: 'Invite User',
                    icon: 'pi pi-user-plus text-info',
                    to: '/organization/undefined/businesses',
                    inlineIconNav: true,
                    command: () => {
                        navigateToRoute(
                            Routes.Business,
                            { id: docuStore.getActiveOrganizationId },
                            'UserCreateDialog-open',
                        )
                    },
                    display: () => docuStore.getPermissionToOrganization('biz_invite_create'),
                },
            ],
        },
        {
            label: 'Projects',
            icon: 'pi pi-fw pi-bookmark text-info',
            to: `/organization/undefined/projects`,
            command: () => {
                navigateToRoute(Routes.Projects, { id: docuStore.getActiveOrganizationId })
            },
            display: () => docuStore.getPermissionToOrganization('project_view'),
            items: [
                {
                    label: 'Create Project',
                    icon: 'pi pi-folder-plus text-info',
                    to: '/organization/undefined/projects',
                    inlineIconNav: true,
                    command: () => {
                        navigateToRoute(
                            Routes.Projects,
                            { id: docuStore.getActiveOrganizationId },
                            'ProjectCreateDialog-open',
                        )
                    },
                    display: () => docuStore.getPermissionToOrganization('project_create'),
                },
            ],
        },
        {
            label: 'Collections',
            icon: 'pi pi-fw pi-folder text-info',
            to: `/organization/undefined/folders`,
            command: () => {
                navigateToRoute(Routes.Folders, { id: docuStore.getActiveOrganizationId })
            },
            display: () => docuStore.getPermissionToOrganization('folder_view'),
            items: [
                {
                    label: 'Create Collection',
                    icon: 'pi pi-folder-plus text-info',
                    to: '/organization/undefined/folders',
                    inlineIconNav: true,
                    command: () => {
                        navigateToRoute(
                            Routes.Folders,
                            { id: docuStore.getActiveOrganizationId },
                            'CollectionCreateDialog-open',
                        )
                    },
                    display: () => docuStore.getPermissionToOrganization('folder_create'),
                },
            ],
        },
        {
            label: 'Users',
            icon: 'pi pi-fw pi-users text-info',
            to: `/organization/undefined/users`,
            badge: undefined,
            command: () => {
                //
            },
            display: false,
            items: [
                {
                    label: 'My Team',
                    icon: 'pi pi-user-edit text-info',
                    to: `/organization/undefined/users`,
                    command: () => {
                        navigateToRoute(Routes.ManageUserOrganization, {
                            id: docuStore.getActiveOrganizationId,
                        })
                    },
                    display: () => docuStore.getPermissionToOrganization('org_invite_view'),
                    items: [
                        {
                            label: 'Invite User',
                            icon: 'pi pi-user-plus text-info',
                            to: `/organization/${docuStore.getActiveOrganizationId}/users/create`,
                            inlineIconNav: true,
                            command: () => {
                                navigateToRoute(
                                    Routes.ManageUserOrganization,
                                    { id: docuStore.getActiveOrganizationId },
                                    'UserCreateDialog-open',
                                )
                            },
                            display: () => docuStore.getPermissionToOrganization('org_invite_create'),
                        },
                    ],
                },
                {
                    label: 'Customers',
                    icon: 'pi pi-user-edit text-info',
                    to: `/organization/undefined/users/businesses`,
                    command: () => {
                        navigateToRoute(Routes.ManageUserBusinesses, {
                            id: docuStore.getActiveOrganizationId,
                        })
                    },
                    display: () => docuStore.getPermissionToOrganization('biz_invite_view'),
                    items: [
                        {
                            label: 'Invite User',
                            icon: 'pi pi-user-plus text-info',
                            to: `/organization/undefined/users/create`,
                            inlineIconNav: true,
                            command: () => {
                                navigateToRoute(
                                    Routes.ManageUserBusinesses,
                                    { id: docuStore.getActiveOrganizationId },
                                    'UserCreateDialog-open',
                                )
                            },
                            display: () => docuStore.getPermissionToOrganization('biz_invite_create'),
                        },
                    ],
                },
                {
                    label: 'Invitations',
                    icon: 'pi pi-cog text-info',
                    to: `/organization/undefined/users/invitations`,
                    inlineIconNav: true,
                    command: () => {
                        navigateToRoute(Routes.ManageInvitations, { id: docuStore.getActiveOrganizationId })
                    },
                    display: () => docuStore.getPermissionToOrganization('biz_invite_view'),
                },
                {
                    label: 'Requests',
                    icon: 'pi pi-id-card text-info',
                    to: `/organization/undefined/users/requests`,
                    inlineIconNav: true,
                    badge: docuStore
                        .getOrganizationInformation(docuStore.getActiveOrganizationId, 'users_requesting_access_count')
                        .toLocaleString(),
                    command: () => {
                        navigateToRoute(Routes.ManageRequests, { id: docuStore.getActiveOrganizationId })
                    },
                    display: () => docuStore.getPermissionToOrganization('biz_invite_create'),
                },
            ],
        },
    ])

    function subtractFromStoreBadge(label: string, amount: number) {
        const navItem = navbar.find((item) => item.label.toLowerCase() === label.toLowerCase())
        if (!navItem) return
        let currentCount = Number((navItem.badge || '').replace(/,/g, '')) || 0
        currentCount = Math.max(0, currentCount - amount)
        navItem.badge = currentCount > 0 ? currentCount.toLocaleString() : ''
    }

    function setNavbarItemVisibility(label: string, display: boolean) {
        const item = navbar.find((nav) => nav.label === label)
        if (item) {
            item.display = display
        }
    }

    function toggleItem(itemKey: string) {
        const wasExpanded = !!expandedItems.value[itemKey]
        // Determine the level (number of segments)
        const segments = itemKey.split('/')
        const level = segments.length
        const parentKey = level > 1 ? segments.slice(0, -1).join('/') : null

        // Collapse all keys that are on the same level and share the same parent.
        Object.keys(expandedItems.value).forEach((key) => {
            const keySegments = key.split('/')
            const keyLevel = keySegments.length
            if (keyLevel === level) {
                // For top-level items, collapse all - for nested items, collapse only siblings (i.e. same parentKey)
                if (!parentKey || keySegments.slice(0, -1).join('/') === parentKey) {
                    expandedItems.value[key] = false
                }
            }
        })

        // If the clicked item wasn't already expanded, set it to true.
        if (!wasExpanded) {
            expandedItems.value[itemKey] = true
        }
    }

    function displayAllNavbar(display: boolean) {
        setNavbarItemVisibility('Businesses', display)
        setNavbarItemVisibility('Projects', display)
        setNavbarItemVisibility('Collections', display)
        setNavbarItemVisibility('Users', display)
        if (display) {
            setNavbarItemVisibility('Org', false)
            setNavbarItemVisibility('Resources', false)
            setNavbarItemVisibility('Contact Us', false)
        } else {
            if (docuStore.getOrganizationsCount === 0 && pageProps.value.auth.user) {
                setNavbarItemVisibility('Org', true)
            }
            setNavbarItemVisibility('Resources', true)
            setNavbarItemVisibility('Contact Us', true)
        }
    }

    return { navbar, setNavbarItemVisibility, displayAllNavbar, subtractFromStoreBadge, expandedItems, toggleItem }
})
